// Library methods
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";

// MUI Components
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "../../../../components/UI/Alert";
import SearchBox from "../../../../components/UI/SearchBox";
import CustomDatePicker from "../../../../components/UI/CustomDatePIcker";
import DefaultModal from "../../../../components/UI/DefaultModal";
import CustomSwitch from "../../../../components/UI/CustomSwitch";

// Styles
import {
  CustomTextFieldOutlined,
  CustomTextFieldOutlinedInputProps,
  ModalCustomCancel,
  ModalCustomConfirm,
  ModalCustomWarning,
} from "../../../../styles/muiStylesHelper";

// Utilities
import { getLocale } from "../../../../utils/localeHelper";
import { calculateYearDifference } from "../../../../utils/dateHelper";
import { allLanguageMap } from "../../../../i18n/languages";
import useClinic from "../../../../hooks/useClinic";
import { MAX_RX_AXIS_VALUE, MIN_RX_AXIS_VALUE } from "../../../../enums";

const AddPatientModal = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  firstName,
  lastName,
  healthCareId,
  birthDate,
  birthDateError,
  sex,
  language,
  setFirstName,
  setLastName,
  setHealthCareId,
  setBirthDate,
  setBirthDateError,
  setSex,
  setLanguage,
  requiresCaptions,
  setRequiresCaptions,
  OSPrescription,
  ODPrescription,
  setOSPrescription,
  setODPrescription,
  checkPatientName,
  existingPatient,
  setExistingPatient,
  onConfirmNavigation,
  sortedLanguageList = [],
  genderData = [],
}) => {
  // internationalization
  const { t, i18n } = useTranslation();
  const { clinicTimezone } = useClinic();

  const existingBirthDate = useMemo(
    () =>
      existingPatient?.dateOfBirth
        ? existingPatient.dateOfBirth.slice(0, 10)
        : null,
    [existingPatient?.dateOfBirth]
  );

  const calculatedAgeText = useMemo(
    () =>
      birthDate && calculateYearDifference(birthDate.toUTC().toISO()) !== null
        ? `${calculateYearDifference(birthDate.toUTC().toISO())} ${t("patients_modal_age")}`
        : null,
    [birthDate, t]
  );

  const handleChange = useCallback(
    (newValue) => {
      // Set the new value to the correct timezone and start of the day
      const newValueInZone = newValue?.setZone(clinicTimezone)?.startOf("day");

      // Set local time to the start of the day
      const today = DateTime.now().setZone(clinicTimezone).startOf("day");

      // set min date to 120 years ago
      const minDate = today.minus({ years: 120 });

      if (!newValueInZone || !newValueInZone?.isValid) {
        setBirthDateError(2);
      } else if (newValueInZone > today) {
        setBirthDateError(1);
      } else if (newValueInZone < minDate) {
        setBirthDateError(4);
      } else {
        setBirthDateError(false);
      }

      // allows the age display to be updated even with an error message
      setBirthDate(newValueInZone);
    },
    [setBirthDate, setBirthDateError, clinicTimezone]
  );

  const shouldDisable = useMemo(() => {
    let isIncluded = 0;
    Object.keys(allLanguageMap).forEach((key) => {
      if (t(allLanguageMap[key]?.val) === language?.val) isIncluded++;
    });
    genderData.forEach((gender) => {
      if (gender?.val === sex?.val) isIncluded++;
    });
    return !(firstName && lastName && birthDate && sex) || isIncluded !== 2;
  }, [t, birthDate, firstName, language, lastName, sex, genderData]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={onClose}
      title={t("patients_modal_title")}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "20px",
          }}
        >
          <Button variant="text" sx={ModalCustomCancel} onClick={onCancel}>
            {t("button_cancel")}
          </Button>
          <Button
            variant="outlined"
            sx={ModalCustomConfirm}
            color="primary"
            disabled={shouldDisable || birthDateError}
            onClick={onConfirm}
          >
            {t("button_add")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="20px" gap="20px">
        {existingPatient && (
          <Alert
            variant="warning"
            alertContent={
              <Grid display="flex" flexDirection="column" gap="8px">
                <Typography>
                  {t("patients_modal_already_exists_warning")}
                </Typography>
                <Grid
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid display="flex" flexDirection="column">
                    <Typography fontSize="14px">
                      {t("patients_modal_health_care_id")} {":"}{" "}
                      {existingPatient?.healthCareId || "N/A"}
                    </Typography>
                    <Typography fontSize="14px">
                      {t("patients_modal_birth")} {":"}{" "}
                      {existingBirthDate || "N/A"}
                    </Typography>
                  </Grid>
                  <Button
                    variant="outlined"
                    sx={ModalCustomWarning}
                    onClick={onConfirmNavigation}
                  >
                    {t("word_view")}
                  </Button>
                </Grid>
              </Grid>
            }
          />
        )}

        {/* First Name and Last Name fields */}
        <Box display="flex" gap="20px">
          <TextField
            id="outlined-basic"
            required
            value={firstName}
            onChange={(e) => {
              setExistingPatient(null);
              setFirstName(e.target.value);
            }}
            onBlur={() => checkPatientName(firstName, lastName)}
            label={t("patients_modal_first_name")}
            variant="outlined"
            fullWidth
            sx={() => CustomTextFieldOutlined({ mt: 0, mr: 0 })}
            InputProps={{
              sx: CustomTextFieldOutlinedInputProps,
            }}
            color="warning"
          />
          <TextField
            id="outlined-basic"
            required
            value={lastName}
            onChange={(e) => {
              setExistingPatient(null);
              setLastName(e.target.value);
            }}
            onBlur={() => checkPatientName(firstName, lastName)}
            label={t("patients_modal_last_name")}
            variant="outlined"
            fullWidth
            sx={() => CustomTextFieldOutlined({ my: 0 })}
            InputProps={{
              sx: CustomTextFieldOutlinedInputProps,
            }}
            color="warning"
          />
        </Box>
        <TextField
          id="outlined-basic"
          value={healthCareId}
          onChange={(e) => setHealthCareId(e.target.value)}
          label={t("patients_modal_health_care_id")}
          variant="outlined"
          fullWidth
          sx={() => CustomTextFieldOutlined({ mb: 0 })}
          InputProps={{
            sx: CustomTextFieldOutlinedInputProps,
          }}
          color="warning"
        />

        {/* Birthdate selector */}
        <Box
          width="100%"
          display="flex"
          flexDirection="row"
          gap="20px"
          alignItems="center"
        >
          <Box width="100%">
            <LocalizationProvider
              dateAdapter={AdapterLuxon}
              adapterLocale={getLocale(i18n.language).locale}
            >
              <CustomDatePicker
                date={birthDate}
                birthDateError={birthDateError}
                handleChange={handleChange}
                clinicTimezone={clinicTimezone}
              />
            </LocalizationProvider>
          </Box>

          {calculatedAgeText && (
            <Box minWidth="fit-content">
              <Typography data-testid="patient_age_display" variant="body2">
                {calculatedAgeText}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Gender selector */}
        <SearchBox
          data={genderData}
          state={sex}
          setState={setSex}
          label={t("patients_modal_sex")}
          propertyBit={2}
          mb={0}
        />
        <SearchBox
          data={sortedLanguageList}
          state={language}
          setState={setLanguage}
          label={t("patients_modal_language")}
          mb={0}
        />

        {/* Prescription fields */}
        <Box display="flex" flexDirection="column" gap="4px">
          <Typography
            data-testid="patient_od_prescription_display"
            variant="body2"
          >
            {t("OD Rx")}
          </Typography>
          <Box display="flex" gap="20px">
            <TextField
              type="number"
              id="od-sphere"
              value={ODPrescription.sphere}
              onChange={(e) => {
                setExistingPatient(null);
                setODPrescription((prev) => ({
                  ...prev,
                  sphere: e.target.value,
                }));
              }}
              label={t("exams_modal_sphere")}
              variant="outlined"
              fullWidth
              sx={() => CustomTextFieldOutlined({ my: 0 })}
              InputProps={{
                sx: CustomTextFieldOutlinedInputProps,
              }}
              color="warning"
            />
            <TextField
              type="number"
              id="od-cylinder"
              value={ODPrescription.cylinder}
              onChange={(e) => {
                setExistingPatient(null);
                setODPrescription((prev) => ({
                  ...prev,
                  cylinder: e.target.value,
                }));
              }}
              label={t("exams_modal_cylinder")}
              variant="outlined"
              fullWidth
              sx={() => CustomTextFieldOutlined({ mt: 0, mr: 0 })}
              InputProps={{
                sx: CustomTextFieldOutlinedInputProps,
              }}
              color="warning"
            />
            <TextField
              type="number"
              id="od-axis"
              value={ODPrescription.axis}
              onChange={(e) => {
                setExistingPatient(null);
                setODPrescription((prev) => ({
                  ...prev,
                  axis: e.target.value,
                }));
              }}
              onBlur={(event) => {
                const newAxisValue = Number(event?.target?.value);
                if (newAxisValue > MAX_RX_AXIS_VALUE) {
                  setODPrescription((prev) => ({
                    ...prev,
                    axis: MAX_RX_AXIS_VALUE,
                  }));
                } else if (newAxisValue < MIN_RX_AXIS_VALUE) {
                  setODPrescription((prev) => ({
                    ...prev,
                    axis: MIN_RX_AXIS_VALUE,
                  }));
                } else {
                  setODPrescription((prev) => ({
                    ...prev,
                    axis: event.target.value === "" ? "" : newAxisValue,
                  }));
                }
              }}
              label={t("exams_modal_axis")}
              variant="outlined"
              fullWidth
              sx={() => CustomTextFieldOutlined({ my: 0 })}
              InputProps={{
                inputProps: { min: MIN_RX_AXIS_VALUE, max: MAX_RX_AXIS_VALUE },
                sx: CustomTextFieldOutlinedInputProps,
              }}
              color="warning"
            />
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap="4px">
          <Typography
            data-testid="patient_os_prescription_display"
            variant="body2"
          >
            {t("OS Rx")}
          </Typography>
          <Box display="flex" gap="20px">
            <TextField
              type="number"
              id="os-sphere"
              value={OSPrescription.sphere}
              onChange={(e) => {
                setExistingPatient(null);
                setOSPrescription((prev) => ({
                  ...prev,
                  sphere: e.target.value,
                }));
              }}
              label={t("exams_modal_sphere")}
              variant="outlined"
              fullWidth
              sx={() => CustomTextFieldOutlined({ my: 0 })}
              InputProps={{
                sx: CustomTextFieldOutlinedInputProps,
              }}
              color="warning"
            />
            <TextField
              type="number"
              id="os-cylinder"
              value={OSPrescription.cylinder}
              onChange={(e) => {
                setExistingPatient(null);
                setOSPrescription((prev) => ({
                  ...prev,
                  cylinder: e.target.value,
                }));
              }}
              label={t("exams_modal_cylinder")}
              variant="outlined"
              fullWidth
              sx={() => CustomTextFieldOutlined({ mt: 0, mr: 0 })}
              InputProps={{
                sx: CustomTextFieldOutlinedInputProps,
              }}
              color="warning"
            />
            <TextField
              type="number"
              id="os-axis"
              value={OSPrescription.axis}
              onChange={(e) => {
                setExistingPatient(null);
                setOSPrescription((prev) => ({
                  ...prev,
                  axis: e.target.value,
                }));
              }}
              onBlur={(event) => {
                const newAxisValue = Number(event?.target?.value);
                if (newAxisValue > MAX_RX_AXIS_VALUE) {
                  setOSPrescription((prev) => ({
                    ...prev,
                    axis: MAX_RX_AXIS_VALUE,
                  }));
                } else if (newAxisValue < MIN_RX_AXIS_VALUE) {
                  setOSPrescription((prev) => ({
                    ...prev,
                    axis: MIN_RX_AXIS_VALUE,
                  }));
                } else {
                  setOSPrescription((prev) => ({
                    ...prev,
                    axis: event.target.value === "" ? "" : newAxisValue,
                  }));
                }
              }}
              label={t("exams_modal_axis")}
              variant="outlined"
              fullWidth
              sx={() => CustomTextFieldOutlined({ my: 0 })}
              InputProps={{
                inputProps: { min: MIN_RX_AXIS_VALUE, max: MAX_RX_AXIS_VALUE },
                sx: CustomTextFieldOutlinedInputProps,
              }}
              color="warning"
            />
          </Box>
        </Box>

        <FormControlLabel
          control={
            <CustomSwitch
              checked={requiresCaptions}
              onChange={(e) => setRequiresCaptions(e.target.checked)}
            />
          }
          label={t("exam_captions")}
        />
      </Grid>
    </DefaultModal>
  );
};

export default AddPatientModal;
